import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { EthAddr } from '@shared/SchemaTypes'
import { fetchOptimizedAPYDataForSierra, fetchOptimizedCurrentAPYForSierra } from '@react/fetchers/apyOptimizedFetcher'
import { fetchDailyPoolOverviewCTOs, fetchPoolOverviewCTO, poolOffchainDataFetch } from '@react/fetchers/PoolFetchers'
import { fetchLytPoolReserve, fetchReserveActionPlanStateDataCTO } from '@react/lyt/LytFetchers'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { LytReservesCTO } from '@shared/LytReservesCTO'
import { displayName } from '@shared/PoolBaseCTO'
import dayjs from 'dayjs'

interface APYGraphData {
  timestamp: number
  value: number
  month: string
}

interface APYState {
  currentAPY: string | null
  graphData: Record<string, APYGraphData[]>
  isLoading: boolean
  isBackgroundRefreshing: boolean
  lastUpdated: number | null
  error: string | null
}

interface ReservesState {
  reserves: LytReservesCTO[]
  reservesWithRates: (LytReservesCTO & { indicativeRate?: string; displayName?: string; yieldType?: string })[]
  isLoading: boolean
  lastUpdated: number | null
  error: string | null
}

const initialAPYState: APYState = {
  currentAPY: null,
  graphData: {},
  isLoading: false,
  isBackgroundRefreshing: false,
  lastUpdated: null,
  error: null,
}

const initialReservesState: ReservesState = {
  reserves: [],
  reservesWithRates: [],
  isLoading: false,
  lastUpdated: null,
  error: null,
}

const aggregateToWeekly = (dailyData: Array<{ timestamp: number; value: number }>) => {
  if (dailyData.length === 0) return []

  const weeklyData: Array<{ timestamp: number; value: number; month: string }> = []
  const WEEK_IN_SECONDS = 7 * 24 * 60 * 60

  const sortedData = [...dailyData].sort((a, b) => a.timestamp - b.timestamp)

  const now = new Date()
  const todayTimestamp = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000)

  const findClosestDataBefore = (targetTimestamp: number) => {
    let closestData = null
    let closestDistance = Infinity

    for (const item of sortedData) {
      if (item.timestamp <= targetTimestamp) {
        const distance = targetTimestamp - item.timestamp
        if (distance < closestDistance && !isNaN(item.value) && item.value !== null) {
          closestDistance = distance
          closestData = item
        }
      }
    }
    return closestData
  }

  let currentTargetDate = todayTimestamp
  const earliestTimestamp = sortedData[0].timestamp

  while (currentTargetDate >= earliestTimestamp) {
    const closestData = findClosestDataBefore(currentTargetDate)

    if (closestData) {
      weeklyData.push({
        timestamp: currentTargetDate,
        value: closestData.value,
        month: dayjs(currentTargetDate * 1000).format('MMM DD'),
      })
    }

    currentTargetDate -= WEEK_IN_SECONDS
  }

  return weeklyData.sort((a, b) => a.timestamp - b.timestamp)
}

export const fetchCurrentAPY = createAsyncThunk(
  'apy/fetchCurrentAPY',
  async (params: { serverApiBaseUrl: string; restAPIKey: string; poolAddr: EthAddr; accountAddr?: EthAddr }) => {
    const { serverApiBaseUrl, restAPIKey, poolAddr, accountAddr } = params
    const isSierraApp = location.hostname.includes('sierra')

    if (isSierraApp) {
      const optimizedAPY = await fetchOptimizedCurrentAPYForSierra(serverApiBaseUrl, restAPIKey, poolAddr, accountAddr)
      if (optimizedAPY !== null) {
        return optimizedAPY
      }
    }

    const reserveAllocation = await fetchLytPoolReserve(serverApiBaseUrl, restAPIKey, poolAddr)
    const reserves = reserveAllocation.reserves

    if (!reserves || reserves.length === 0) {
      return null
    }

    const start = new Date()
    start.setFullYear(start.getFullYear() - 1)
    const startDayStr = start.toISOString().slice(0, 10)

    const allPromises = reserves.map((reserve) =>
      fetchDailyPoolOverviewCTOs(serverApiBaseUrl, restAPIKey, reserve.poolAddr, startDayStr, undefined)
        .then((result) =>
          result.map((d: any) => ({
            assetValue: parseFloat(erc20WeiToCommaDecString(reserve.assetBalance)),
            timestamp: d.timestamp,
            apy: d.indicativeInterestRate
              ? Number(d.indicativeInterestRate) / 100
              : d.interestRate
                ? Number(d.interestRate) / 100
                : 0,
          }))
        )
        .catch(() => [])
    )

    const allAssetHistoricalData = await Promise.all(allPromises)
    const flatData = allAssetHistoricalData.flat()

    const groupedByTimestamp = flatData.reduce(
      (acc, item) => {
        if (!acc[item.timestamp]) acc[item.timestamp] = []
        acc[item.timestamp].push(item)
        return acc
      },
      {} as Record<number, Array<{ assetValue: number; timestamp: number; apy: number }>>
    )

    const weightedAverageData = Object.entries(groupedByTimestamp)
      .map(([timestamp, assets]) => {
        const totalValue = assets.reduce((sum, asset) => sum + asset.assetValue, 0)
        const weightedSum = assets.reduce((sum, asset) => sum + asset.apy * asset.assetValue, 0)
        const weightedAverage = totalValue > 0 ? weightedSum / totalValue : 0

        return { timestamp: parseInt(timestamp), value: weightedAverage }
      })
      .sort((a, b) => a.timestamp - b.timestamp)

    const weeklyData = aggregateToWeekly(weightedAverageData)

    if (weeklyData.length > 0) {
      const latestAPY = weeklyData[weeklyData.length - 1].value
      return latestAPY.toFixed(2)
    }

    return null
  }
)

export const fetchAPYGraphData = createAsyncThunk(
  'apy/fetchAPYGraphData',
  async (params: {
    serverApiBaseUrl: string
    restAPIKey: string
    poolAddr: EthAddr
    timeFilter: '1M' | '3M' | '6M' | '1Y'
    accountAddr?: EthAddr
  }) => {
    const { serverApiBaseUrl, restAPIKey, poolAddr, timeFilter, accountAddr } = params
    const isSierraApp = location.hostname.includes('sierra')

    if (isSierraApp) {
      const optimizedData = await fetchOptimizedAPYDataForSierra(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        timeFilter,
        accountAddr
      )

      if (optimizedData.length > 0) {
        return { timeFilter, data: optimizedData }
      }
    }

    const reserveAllocation = await fetchLytPoolReserve(serverApiBaseUrl, restAPIKey, poolAddr)
    const reserves = reserveAllocation.reserves

    if (!reserves || reserves.length === 0) {
      return { timeFilter, data: [] }
    }

    const start = new Date()
    switch (timeFilter) {
      case '1M':
        start.setMonth(start.getMonth() - 1)
        break
      case '3M':
        start.setMonth(start.getMonth() - 3)
        break
      case '6M':
        start.setMonth(start.getMonth() - 6)
        break
      case '1Y':
        start.setFullYear(start.getFullYear() - 1)
        break
    }
    const startDayStr = start.toISOString().slice(0, 10)

    const allPromises = reserves.map((reserve) =>
      fetchDailyPoolOverviewCTOs(serverApiBaseUrl, restAPIKey, reserve.poolAddr, startDayStr, undefined)
        .then((result) =>
          result.map((d: any) => ({
            assetValue: parseFloat(erc20WeiToCommaDecString(reserve.assetBalance)),
            timestamp: d.timestamp,
            apy: d.indicativeInterestRate
              ? Number(d.indicativeInterestRate) / 100
              : d.interestRate
                ? Number(d.interestRate) / 100
                : 0,
          }))
        )
        .catch(() => [])
    )

    const allAssetHistoricalData = await Promise.all(allPromises)
    const flatData = allAssetHistoricalData.flat()

    const groupedByTimestamp = flatData.reduce(
      (acc, item) => {
        if (!acc[item.timestamp]) acc[item.timestamp] = []
        acc[item.timestamp].push(item)
        return acc
      },
      {} as Record<number, Array<{ assetValue: number; timestamp: number; apy: number }>>
    )

    const weightedAverageData = Object.entries(groupedByTimestamp)
      .map(([timestamp, assets]) => {
        const totalValue = assets.reduce((sum, asset) => sum + asset.assetValue, 0)
        const weightedSum = assets.reduce((sum, asset) => sum + asset.apy * asset.assetValue, 0)
        const weightedAverage = totalValue > 0 ? weightedSum / totalValue : 0

        return { timestamp: parseInt(timestamp), value: weightedAverage }
      })
      .sort((a, b) => a.timestamp - b.timestamp)

    const weeklyData = aggregateToWeekly(weightedAverageData)

    return { timeFilter, data: weeklyData }
  }
)

export const fetchReserves = createAsyncThunk(
  'transparency/fetchReserves',
  async (params: { serverApiBaseUrl: string; restAPIKey: string; poolAddr: EthAddr }, { getState }) => {
    const { serverApiBaseUrl, restAPIKey, poolAddr } = params
    const state = getState() as any
    const stageConfiguration = state.chainState.stageConfiguration

    const reserveActionPlan = await fetchReserveActionPlanStateDataCTO(serverApiBaseUrl, restAPIKey, poolAddr)
    const reserves = reserveActionPlan.reserveAllocationsWithWithdrawalsState

    const otServerApiBaseUrl = stageConfiguration?.parentStageConfiguration?.serverApiBaseUrl || serverApiBaseUrl
    const otRestAPIKey = stageConfiguration?.parentStageConfiguration?.restAPIKey || restAPIKey

    const reservesWithIndicativeRates = await Promise.all(
      reserves.map(async (reserve) => {
        try {
          const [poolOverview, poolOffchainData] = await Promise.all([
            fetchPoolOverviewCTO(serverApiBaseUrl, restAPIKey, reserve.poolAddr),
            poolOffchainDataFetch(otServerApiBaseUrl, otRestAPIKey, reserve.poolAddr).catch(() => null),
          ])
          let indicativeRate = '0'
          if (poolOverview) {
            if ('indicativeInterestRate' in poolOverview) {
              indicativeRate = poolOverview.indicativeInterestRate
            } else if ('interestRate' in poolOverview) {
              indicativeRate = poolOverview.interestRate
            }
          }

          const reserveDisplayName = poolOffchainData?.displayName
            ? poolOffchainData.displayName
            : poolOverview
              ? displayName(poolOverview)
              : reserve.poolName

          const reserveYieldType = reserveDisplayName?.includes('Liquidity Buffer')
            ? 'N/A'
            : poolOffchainData?.yieldType

          return {
            ...reserve,
            indicativeRate,
            displayName: reserveDisplayName,
            yieldType: reserveYieldType,
          }
        } catch (_error) {
          return {
            ...reserve,
            indicativeRate: '0',
            displayName: reserve.poolName,
            yieldType: 'N/A',
          }
        }
      })
    )

    return { reserves, reservesWithRates: reservesWithIndicativeRates }
  }
)

const apySlice = createSlice({
  name: 'apy',
  initialState: initialAPYState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCache: (state) => {
      state.currentAPY = null
      state.graphData = {}
      state.lastUpdated = null
    },
    setBackgroundRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isBackgroundRefreshing = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentAPY.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCurrentAPY.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentAPY = action.payload
        state.lastUpdated = Date.now()
      })
      .addCase(fetchCurrentAPY.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch APY'
      })
      .addCase(fetchAPYGraphData.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAPYGraphData.fulfilled, (state, action) => {
        state.isLoading = false
        const { timeFilter, data } = action.payload
        state.graphData[timeFilter] = data
        state.lastUpdated = Date.now()
      })
      .addCase(fetchAPYGraphData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch graph data'
      })
  },
})

const reservesSlice = createSlice({
  name: 'reserves',
  initialState: initialReservesState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCache: (state) => {
      state.reserves = []
      state.reservesWithRates = []
      state.lastUpdated = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReserves.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchReserves.fulfilled, (state, action) => {
        state.isLoading = false
        state.reserves = action.payload.reserves
        state.reservesWithRates = action.payload.reservesWithRates
        state.lastUpdated = Date.now()
      })
      .addCase(fetchReserves.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch reserves'
      })
  },
})

export const { clearError, clearCache, setBackgroundRefreshing } = apySlice.actions
export const { clearError: clearReservesError, clearCache: clearReservesCache } = reservesSlice.actions

export default apySlice.reducer
export const reservesReducer = reservesSlice.reducer

export const selectCurrentAPY = (state: { apy: APYState }) => state.apy.currentAPY
export const selectAPYGraphData = (state: { apy: APYState }, timeFilter: string) =>
  state.apy.graphData[timeFilter] || []
export const selectAPYLoading = (state: { apy: APYState }) => state.apy.isLoading
export const selectAPYBackgroundRefreshing = (state: { apy: APYState }) => state.apy.isBackgroundRefreshing
export const selectAPYError = (state: { apy: APYState }) => state.apy.error
export const selectAPYLastUpdated = (state: { apy: APYState }) => state.apy.lastUpdated

export const selectReserves = (state: { reserves: ReservesState }) => state.reserves.reserves
export const selectReservesWithRates = (state: { reserves: ReservesState }) => state.reserves.reservesWithRates
export const selectReservesLoading = (state: { reserves: ReservesState }) => state.reserves.isLoading
export const selectReservesError = (state: { reserves: ReservesState }) => state.reserves.error
export const selectReservesLastUpdated = (state: { reserves: ReservesState }) => state.reserves.lastUpdated

export const shouldRefreshAPY = (lastUpdated: number | null, maxAge: number = 5 * 60 * 1000) => {
  if (!lastUpdated) return true
  return Date.now() - lastUpdated > maxAge
}
